import React, { useEffect, useState, useCallback } from 'react';
import styles from './ScrimYoutubePlayers.module.css';
import ScrimYoutubePlayer from './ScrimYoutubePlayer';

interface VideoInfo {
  id: string;
  startTime: Date;
  duration: number;
  title: string;
}

type VideoQuality = 'hd1080' | 'hd720';

interface ScrimYoutubePlayersProps {
  videoInfos: VideoInfo[];
  globalStartTime: Date | null;
  isPlaying: boolean;
  currentTime: number;
  viewMode: 'grid' | 'single';
  activeVideoIndex: number;
  onActiveVideoChange: (index: number) => void;
  onPlayersReady: (ready: boolean) => void;
  onTimeUpdate: (time: number) => void;
  quality: VideoQuality;
  onRequestPlay?: () => void;
  allReady?: boolean;
  volume: number; // Add volume prop
  onTrackSyncChange?: (videoId: string, offsetChange: number) => void; // Sync change callback
  onAddTrackClick?: () => void; // Add track callback
}

const ScrimYoutubePlayers: React.FC<ScrimYoutubePlayersProps> = ({
  videoInfos,
  globalStartTime,
  isPlaying,
  currentTime,
  viewMode,
  activeVideoIndex,
  onActiveVideoChange,
  onPlayersReady,
  onTimeUpdate,
  quality,
  onRequestPlay,
  allReady,
  volume,
  onTrackSyncChange,
  onAddTrackClick
}) => {
  const [playerStates, setPlayerStates] = useState<{ [key: string]: { ready: boolean; playing: boolean } }>({});
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  const [initialOverlayVisible, setInitialOverlayVisible] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    const stored = localStorage.getItem('scrim_sidebar_expanded');
    return stored ? JSON.parse(stored) : true;
  });

  // Save sidebar expansion state to localStorage
  useEffect(() => {
    localStorage.setItem('scrim_sidebar_expanded', JSON.stringify(isSidebarExpanded));
  }, [isSidebarExpanded]);

  const toggleSidebarExpanded = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  // Utility function to trim title
  const trimTitle = (title: string): string => {
    if (title.length <= 24) return title;
    return title.substring(0, 24);
  };

  // Calculate individual player times based on global time and their offsets
  const calculatePlayerTime = useCallback((videoInfo: VideoInfo, globalTime: number): number => {
    if (!globalStartTime) return 0;
    
    // Calculate offset: how many seconds after global start this video started
    const offsetSeconds = (videoInfo.startTime.getTime() - globalStartTime.getTime()) / 1000;
    
    // Calculate the time for this specific player
    const playerTime = globalTime - offsetSeconds;
    
    // Ensure time is not negative (video hasn't started yet)
    return Math.max(0, playerTime);
  }, [globalStartTime]);

  // Check if a video should be playing at the current global time
  const shouldVideoPlay = useCallback((videoInfo: VideoInfo, globalTime: number): boolean => {
    if (!globalStartTime || !isPlaying) return false;
    
    const offsetSeconds = (videoInfo.startTime.getTime() - globalStartTime.getTime()) / 1000;
    const videoEndTime = offsetSeconds + videoInfo.duration;
    
    // Video should play if global time is within its active period
    return globalTime >= offsetSeconds && globalTime <= videoEndTime;
  }, [globalStartTime, isPlaying]);

  // Handle player ready state changes (now called after autoplay is handled)
  const handlePlayerReady = useCallback((videoId: string) => {
    setPlayerStates(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], ready: true }
    }));
  }, []);

  // Handle player play state changes
  const handlePlayerPlay = useCallback((videoId: string) => {
    setPlayerStates(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], playing: true }
    }));
  }, []);

  // Handle player pause state changes
  const handlePlayerPause = useCallback((videoId: string) => {
    setPlayerStates(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], playing: false }
    }));
  }, []);

  // Handle time updates from any player (for sync purposes)
  const handlePlayerTimeUpdate = useCallback((videoId: string, playerTime: number) => {
    if (!globalStartTime) return;
    
    const videoInfo = videoInfos.find(v => v.id === videoId);
    if (!videoInfo) return;
    
    // Determine master video based on view mode
    const masterVideoId = viewMode === 'single' && videoInfos[activeVideoIndex] 
      ? videoInfos[activeVideoIndex].id 
      : (videoInfos.length > 0 ? videoInfos[0].id : null);
    
    if (videoId !== masterVideoId) {
      return; // Ignore time updates from non-master players
    }
    
    // Calculate global time from this player's time
    const offsetSeconds = (videoInfo.startTime.getTime() - globalStartTime.getTime()) / 1000;
    const globalTime = playerTime + offsetSeconds;
    
    onTimeUpdate(globalTime);
  }, [videoInfos, globalStartTime, onTimeUpdate, viewMode, activeVideoIndex]);

  // Check if all players are ready (have handled autoplay and ready for user interaction)
  useEffect(() => {
    if (videoInfos.length === 0) {
      onPlayersReady(false);
      return;
    }

    const readyCount = videoInfos.filter(video => 
      playerStates[video.id]?.ready === true
    ).length;
    
    const allReadyComputed = readyCount === videoInfos.length;
    
    onPlayersReady(allReadyComputed);
  }, [playerStates, videoInfos, onPlayersReady]);

  // Initialize or reconcile player states without wiping existing readiness
  useEffect(() => {
    setPlayerStates(prev => {
      const next: { [key: string]: { ready: boolean; playing: boolean } } = { ...prev };
      const currentIds = new Set(videoInfos.map(v => v.id));
      // Remove states for videos that no longer exist
      Object.keys(next).forEach(id => {
        if (!currentIds.has(id)) {
          delete next[id];
        }
      });
      // Add missing entries for new videos, preserve existing readiness
      videoInfos.forEach(video => {
        if (!next[video.id]) {
          next[video.id] = { ready: false, playing: false };
        }
      });
      return next;
    });
  }, [videoInfos]);

  // Manage initial global overlay visibility
  useEffect(() => {
    if (!overlayDismissed && allReady && !isPlaying) {
      setInitialOverlayVisible(true);
    }
  }, [allReady, isPlaying, overlayDismissed]);

  // Once playback starts, never show overlay again
  useEffect(() => {
    if (isPlaying) {
      setOverlayDismissed(true);
      setInitialOverlayVisible(false);
    }
  }, [isPlaying]);

  const handleGlobalPlayClick = useCallback(() => {
    setOverlayDismissed(true);
    setInitialOverlayVisible(false);
    if (onRequestPlay) onRequestPlay();
  }, [onRequestPlay]);

  const safeActiveIndex = Math.min(activeVideoIndex, videoInfos.length - 1);

  // Calculate optimal grid layout based on video count
  const getGridLayoutClass = (videoCount: number): string => {
    if (viewMode !== 'grid') return '';
    
    switch (videoCount) {
      case 1: return styles.grid1x1;
      case 2: return styles.grid2x1;
      case 3: return styles.grid3x1;
      case 4: return styles.grid2x2;
      case 5: return styles.grid3x2; // 3x2 with one empty space
      case 6: return styles.grid3x2;
      default: return styles.gridAuto;
    }
  };

  const showGlobalPlayOverlay = initialOverlayVisible;

  if (videoInfos.length === 0) {
    return <div className={styles.container}>No videos to display</div>;
  }

  const gridLayoutClass = getGridLayoutClass(videoInfos.length);

  return (
    <div className={`${styles.container} ${viewMode === 'single' ? styles.singleMode : styles.gridMode} ${gridLayoutClass}`}>
      {/* Sidebar backdrop for single mode */}
      {viewMode === 'single' && (
        <div className={`${styles.sidebar} ${isSidebarExpanded ? styles.expanded : styles.collapsed}`}>
          <button 
            className={styles.toggleButton} 
            onClick={toggleSidebarExpanded}
            title={isSidebarExpanded ? undefined : "Tracks"}
          >
            <i className={`bi bi-${isSidebarExpanded ? 'chevron-left' : 'collection-play'}`} />
          </button>
          
          <div className={styles.sidebarContent}>
            <div className={styles.sidebarHeader}>
              <h3>
                <i className="bi bi-collection-play"></i>
                Tracks
              </h3>
            </div>
            {/* Empty sidebar content - just a backdrop for floating thumbnails */}
          </div>
        </div>
      )}
      
      {/* Players container - always render all players, position them with CSS */}
      <div className={styles.playersArea}>
        {showGlobalPlayOverlay && (
          <div className={styles.globalPlayOverlay} onClick={handleGlobalPlayClick}>
            <div className={styles.globalPlayBadge}>
              <i className={`bi bi-play-fill ${styles.globalPlayIcon}`}></i>
              <span>Play</span>
            </div>
          </div>
        )}
        {videoInfos.map((videoInfo, index) => {
          const playerTime = calculatePlayerTime(videoInfo, currentTime);
          const shouldPlay = shouldVideoPlay(videoInfo, currentTime);
          const isActive = index === safeActiveIndex;
          const isMaster = viewMode === 'single' ? isActive : index === 0;
          const isThumb = viewMode === 'single' && !isActive;
          
          // Calculate position class based on view mode and index
          let positionClass = '';
          if (viewMode === 'grid') {
            positionClass = styles.gridPosition;
          } else if (viewMode === 'single') {
            if (isActive) {
              positionClass = styles.singleActivePosition;
            } else {
              positionClass = `${styles.singleThumbnailPosition} ${styles[`thumbnailPosition${index}`]}`;
            }
          }
          
          return (
            <div 
              key={videoInfo.id}
              className={`${styles.playerContainer} ${positionClass}`}
              data-player-index={index}
              data-is-active={isActive}
              data-view-mode={viewMode}
            >
              <ScrimYoutubePlayer
                videoId={videoInfo.id}
                startSeconds={playerTime}
                shouldPlay={shouldPlay}
                isMaster={isMaster}
                isThumb={isThumb}
                viewMode={viewMode}
                title={videoInfo.title}
                trackIndex={index}
                onReady={() => handlePlayerReady(videoInfo.id)}
                onPlay={() => handlePlayerPlay(videoInfo.id)}
                onPause={() => handlePlayerPause(videoInfo.id)}
                onTimeUpdate={(time) => handlePlayerTimeUpdate(videoInfo.id, time)}
                onClick={isThumb ? () => onActiveVideoChange(index) : undefined}
                quality={quality}
                onRequestPlay={onRequestPlay}
                allReady={allReady}
                volume={volume}
                onTrackSyncChange={onTrackSyncChange}
              />
            </div>
          );
        })}
        
        {/* Render placeholder for active player in single mode */}
        {viewMode === 'single' && videoInfos.length > 0 && (
          <div 
            key={`active-placeholder-${videoInfos[safeActiveIndex].id}`}
            className={`${styles.playerContainer} ${styles.singleThumbnailPosition} ${styles[`thumbnailPosition${safeActiveIndex}`]} ${styles.activePlaceholder}`}
            data-player-index={safeActiveIndex}
            data-is-active={true}
            data-view-mode={viewMode}
          >
            <div className={styles.activePlaceholderTitle}>
              <div className={styles.activePlaceholderTitleContent}>
                <div className={styles.activePlaceholderTitleIndex}>
                  {safeActiveIndex + 1}
                </div>
                <div className={styles.activePlaceholderTitleText}>
                  {trimTitle(videoInfos[safeActiveIndex].title)}
                </div>
              </div>
            </div>
            <div className={styles.activePlaceholderContent}>
              <div className={styles.activePlaceholderIcon}>
                <i className="bi bi-display"></i>
              </div>
              <div className={styles.activePlaceholderSubtext}>Currently viewing</div>
            </div>
          </div>
        )}

        {/* Render "Add tracks" placeholder in single mode */}
        {viewMode === 'single' && videoInfos.length < 6 && onAddTrackClick && (
          <div 
            key="add-track-placeholder"
            className={`${styles.playerContainer} ${styles.singleThumbnailPosition} ${styles[`thumbnailPosition${videoInfos.length}`]} ${styles.addTrackPlaceholder}`}
            onClick={onAddTrackClick}
            data-view-mode={viewMode}
          >
            <div className={styles.addTrackPlaceholderContent}>
              <div className={styles.addTrackPlaceholderIcon}>
                <i className="bi bi-plus-circle"></i>
              </div>
              <div className={styles.addTrackPlaceholderText}>
                Add track(s)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScrimYoutubePlayers; 